/* eslint-disable no-useless-escape */
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import config from 'config';
import {
  useAuth0,
  withApi,
  validate as externalValidator,
  useForm,
} from 'utils';
import { PolicyContext } from 'utils/usePolicy';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import {
  Typography,
  Dialog,
  DIALOG_TYPES,
  showErrorAlert,
  Spinner,
  Form,
  Row,
  FormField,
  Input,
  Checkbox,
} from '../../alf-design';
import { Guidance } from 'components/layout/login';
import { isCompany } from 'utils/policyType';

function validate(values) {
  const errors = {};
  const isIndividual = values.checkbox === 'individual';
  const isCompany = values.checkbox === 'llc';

  if (isIndividual) {
    if (
      !values.firstName ||
      values.firstName.trim().length < 2 ||
      !/^[A-Za-z\s.,'\-]+$/.test(values.firstName)
    ) {
      errors.firstName = 'First name is required.';
    }

    if (
      !values.lastName ||
      values.lastName.trim().length < 2 ||
      !/^[A-Za-z\s.,'\-]+$/.test(values.lastName)
    ) {
      errors.lastName = 'Last name is required.';
    }
  }

  if (isCompany) {
    if (!values.company || values.company.trim().length < 2) {
      errors.company = 'Company/DBA name is required.';
    }
  }

  if (!values.zip || !/^\d{5}(?:[-\s]\d{4})?$/.test(values.zip)) {
    errors.zip = 'Invalid property zip code.';
  }

  if (!values.policyNumber || values.policyNumber.trim() === '') {
    errors.policyNumber = 'Policy number is required.';
  } else {
    const sanitizedPolicy = values.policyNumber
      .replace(/[^a-zA-Z0-9]/g, '')
      .trim();
    const beginning = sanitizedPolicy.slice(0, 2).toUpperCase();
    const firstChar = sanitizedPolicy.charAt(0).toUpperCase();
    const validPrefixes = ['H3', 'H6', 'HO', 'D1'];
    if (config.dp3Enabled) {
      validPrefixes.push('D3');
    }

    if (!validPrefixes.includes(beginning) && firstChar !== 'S') {
      errors.policyNumber = config.dp3Enabled
        ? 'Only policies beginning with H3, H6, HO, D3, D1, or S can be linked at this time.'
        : 'Only policies beginning with H3, H6, HO, D1, or S can be linked at this time.';
    } else if (!externalValidator.policyNumber(sanitizedPolicy)) {
      errors.policyNumber = 'Invalid policy number.';
    }
  }

  return errors;
}

const trimLeadingSpace = (value) => value.replace(/^\s+/, '');

const LinkPolicy = ({ show, onClose, api, history, fromDashboard }) => {
  const { policies, loading, setLoading, error, selectedPolicy } =
    useContext(PolicyContext);
  const isCompanyDBA = isCompany(selectedPolicy);
  const [errorPolicies, setErrorPolicies] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [newPolicyNumber, setNewPolicyNumber] = useState('');
  const [previousPolicyNumber, setPreviousPolicyNumber] = useState('');
  const { getTokenSilently } = useAuth0();
  const [selected, setSelected] = useState(isCompanyDBA ? 'llc' : 'individual');
  const [blurred, setBlurred] = useState({
    zip: false,
    policyNumber: false,
    firstName: false,
    lastName: false,
    company: false,
  });

  const initState = {
    checkbox: isCompanyDBA ? 'llc' : 'individual',
    firstName: isCompanyDBA ? '' : selectedPolicy?.insured?.firstName,
    lastName: isCompanyDBA ? '' : selectedPolicy?.insured?.lastName,
    company: isCompanyDBA
      ? trimLeadingSpace(selectedPolicy?.organizationName || '')
      : '',
    policyNumber: '',
    zip: '',
  };
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    errors,
    isSubmitting,
    setFormValues,
    setErrors,
  } = useForm(initState, validate, true);

  const markBlurred = (field, e) => {
    setBlurred((prev) => ({ ...prev, [field]: true }));
    handleBlur(e);
  };

  const errorDescription = (err, request) => {
    let description;
    if (err.response) {
      description = err.response.data.error;
    } else if (err.request) {
      description =
        'Network error. Please check your connection and try again.';
    } else {
      description = `Error attempting to fetch your ${request}. Refresh page to try again.`;
    }
    return description;
  };

  const findPolicy = (policyNumber) => {
    const strippedNumber = policyNumber?.replace(/ /g, '').trim();
    return policies.find((p) => {
      const sNumber = p.policyNumber?.replace(/ /g, '').trim();
      return (
        strippedNumber?.includes(sNumber) || sNumber?.includes(strippedNumber)
      );
    });
  };

  const onSubmit = () => {
    handleSubmit(null, (callback) => {
      const { firstName, lastName, company, zip } = values;
      let { policyNumber } = values;
      policyNumber = policyNumber
        .replace(/  +/g, ' ')
        .replace(/[A-Za-z]+$/, '')
        .trim();

      if (selectedPolicy?.policyNumber) {
        setPreviousPolicyNumber(selectedPolicy.policyNumber);
      }

      if (findPolicy(policyNumber)) {
        setErrorPolicies({
          number: 400,
          description: 'The policy has already been linked.',
        });
        callback(null);
      } else {
        setErrorPolicies({});
        api.withAuth
          .postLinkPolicy({
            policyNumber,
            zip,
            ...(selected === 'individual'
              ? { firstName, lastName, company: '' }
              : { firstName: '', lastName: '', company }),
          })
          .then(async () => {
            await getTokenSilently({ ignoreCache: true });
            setNewPolicyNumber(policyNumber);
            setShowSuccess(true);
            callback(null);
            onClose();
          })
          .catch((err) => {
            callback(err);
            if (err.response) {
              if (err.response.status === 400) {
                setErrorPolicies({
                  number: 400,
                  description:
                    'Uh-Oh! The policy information entered does not match our records.',
                });
              } else {
                setErrorPolicies({
                  number: err.response.status,
                  description: err.response.data.error,
                });
              }
            } else if (err.request) {
              setErrorPolicies({
                number: null,
                description:
                  'Network error. Please check your connection and try again.',
              });
            } else {
              setErrorPolicies({
                number: null,
                description: 'Error attempting to link policy to your account.',
              });
            }

            datadogRum.addError(err, {
              event_name: 'account.policy_access.failed',
              operation: 'link_policy',
              http_status: err?.response?.status,
              failure_reason:
                err?.response?.status === 400
                  ? 'policy_information_mismatch'
                  : err?.response
                    ? 'api_error'
                    : err?.request
                      ? 'network_error'
                      : 'unexpected_error',
            });

            datadogLogs.logger.error(`Link Policy - ${policyNumber}`, {
              err,
            });
          });
      }
    });
  };

  const isEmpty = (values) => {
    const { checkbox, firstName, lastName, company, policyNumber, zip } =
      values;

    const requiredFields =
      checkbox === 'individual'
        ? [firstName, lastName, policyNumber, zip]
        : [company, policyNumber, zip];

    return requiredFields.some((v) => !v || v.trim() === '');
  };

  const resetValidation = () => {
    setErrors({});
    setErrorPolicies({});
    setBlurred({
      zip: false,
      policyNumber: false,
      firstName: false,
      lastName: false,
      company: false,
    });
  };

  const handleClose = () => {
    setFormValues(
      {
        policyNumber: initState.policyNumber,
        zip: initState.zip,
        firstName: initState.firstName,
        lastName: initState.lastName,
        company: initState.company,
      },
      false,
    );
    resetValidation();
    onClose();
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    if (previousPolicyNumber) {
      history.replace(`/?policy=${previousPolicyNumber}`);
    } else {
      window.location.reload();
    }
  };

  const handleViewNewPolicy = () => {
    setShowSuccess(false);
    window.location.href = `/?policy=${newPolicyNumber}`;
  };

  const isButtonDisabled =
    isSubmitting || isEmpty(values) || Object.keys(errors).length !== 0;

  const handleCheckbox = (e) => {
    if (selected !== e.target.name) {
      setFormValues(
        {
          policyNumber: initState.policyNumber,
          zip: initState.zip,
          firstName: initState.firstName,
          lastName: initState.lastName,
          company: initState.company,
          checkbox: e.target.name,
        },
        false,
      );
      resetValidation();
      setSelected(e.target.name);
    }
  };

  useEffect(() => {
    if (fromDashboard && show) {
      onClose();
    }
  }, []);

  useEffect(() => {
    if (show) {
      setSelected(isCompanyDBA ? 'llc' : 'individual');
    }
  }, [show, isCompanyDBA]);

  useEffect(() => {
    if (policies) {
      setLoading(false);
    }
  }, [policies, setLoading]);

  useEffect(() => {
    if (error) {
      setLoading(false);
      showErrorAlert({
        message: `${
          error.entity.charAt(0).toUpperCase() + error.entity.slice(1)
        } Error`,
        description: errorDescription(error.err, error.entity),
      });
    }
  }, [error, setLoading]);

  return (
    <>
      <Dialog
        id="link-policy"
        type={DIALOG_TYPES.CONTENT}
        invokingElementId="link-policy-button"
        show={show}
        onClose={handleClose}
        title="Verify Your Existing Policy"
        subtitle="Enter your policy information below to verify your existing policy."
        primaryButtonText={isSubmitting ? 'Verifying...' : 'Verify Policy'}
        secondaryButtonText="Close"
        onPrimaryClick={onSubmit}
        disabledPrimaryBtn={isButtonDisabled}
        onSecondaryClick={handleClose}
        ariaLabel="Link policy"
      >
        {!loading && policies ? (
          <Form onSubmit={onSubmit} fullWidth>
            {errorPolicies.description && (
              <div className="bg-legacy-errorBackground mx-[4px] mb-[20px] p-[13px]">
                <Typography type="error">
                  {errorPolicies.description}
                </Typography>
              </div>
            )}
            <Row>
              <div className="flex flex-row gap-x-[48px] mb-[24px] p-[4px]">
                <Checkbox
                  id="individual"
                  name="individual"
                  className="individual"
                  checked={selected === 'individual'}
                  onChange={handleCheckbox}
                >
                  <Typography className="ml-[30px]" type="caption1">
                    Individual
                  </Typography>
                </Checkbox>
                <Checkbox
                  id="llc"
                  name="llc"
                  className="llc"
                  checked={selected === 'llc'}
                  onChange={handleCheckbox}
                >
                  <Typography className="ml-[30px]" type="caption1">
                    Company/DBA
                  </Typography>
                </Checkbox>
              </div>
              <FormField
                label="Policy Number"
                icon={<Guidance />}
                error={blurred.policyNumber ? errors.policyNumber : ''}
              >
                <Input
                  id="policynumber-input"
                  name="policyNumber"
                  type="text"
                  onChange={(e) => {
                    const sanitizedValue = e.target.value.replace(
                      /[^a-zA-Z0-9]/g,
                      '',
                    );
                    e.target.value = sanitizedValue;
                    setErrorPolicies({});
                    handleChange(e);
                  }}
                  onBlur={(e) => markBlurred('policyNumber', e)}
                  value={values.policyNumber}
                  error={
                    errorPolicies.number === 404 ||
                    (blurred.policyNumber && !!errors.policyNumber)
                  }
                  success={
                    errorPolicies.number !== 404 &&
                    blurred.policyNumber &&
                    !errors.policyNumber
                  }
                />
              </FormField>
              {selected === 'individual' ? (
                <>
                  <FormField
                    label="First Name"
                    error={blurred.firstName ? errors.firstName : ''}
                  >
                    <Input
                      id="first-name"
                      name="firstName"
                      onChange={(e) => {
                        const nextValue = trimLeadingSpace(e.target.value);
                        if (/^[A-Za-z\s.,'\-]*$/.test(nextValue)) {
                          e.target.value = nextValue;
                          handleChange(e);
                        }
                      }}
                      onBlur={(e) => markBlurred('firstName', e)}
                      value={values.firstName}
                      error={blurred.firstName && !!errors.firstName}
                      success={
                        errorPolicies.number !== 404 &&
                        blurred.firstName &&
                        !errors.firstName
                      }
                    />
                  </FormField>
                  <FormField
                    label="Last Name"
                    error={blurred.lastName ? errors.lastName : ''}
                  >
                    <Input
                      name="lastName"
                      id="last-name"
                      onChange={(e) => {
                        const nextValue = trimLeadingSpace(e.target.value);
                        if (/^[A-Za-z\s.,'\-]*$/.test(nextValue)) {
                          e.target.value = nextValue;
                          handleChange(e);
                        }
                      }}
                      onBlur={(e) => markBlurred('lastName', e)}
                      value={values.lastName}
                      error={blurred.lastName && !!errors.lastName}
                      success={
                        errorPolicies.number !== 404 &&
                        blurred.lastName &&
                        !errors.lastName
                      }
                    />
                  </FormField>
                </>
              ) : (
                <FormField
                  label="Company/DBA Name"
                  error={blurred.company ? errors.company : ''}
                >
                  <Input
                    name="company"
                    id="company"
                    onChange={(e) => {
                      e.target.value = trimLeadingSpace(e.target.value);
                      handleChange(e);
                    }}
                    onBlur={(e) => markBlurred('company', e)}
                    value={values.company}
                    error={blurred.company && !!errors.company}
                    success={
                      errorPolicies.number !== 404 &&
                      blurred.company &&
                      !errors.company
                    }
                  />
                </FormField>
              )}
              <FormField
                label="Property Zip Code"
                error={
                  blurred.zip && errors.zip ? 'Invalid property zip code.' : ''
                }
              >
                <Input
                  id="zip-input"
                  name="zip"
                  type="text"
                  maxLength={5}
                  onChange={(e) => {
                    const sanitizedValue = e.target.value.replace(
                      /[^0-9]/g,
                      '',
                    );
                    e.target.value = sanitizedValue;
                    handleChange(e);
                  }}
                  onBlur={(e) => markBlurred('zip', e)}
                  value={values.zip}
                  error={blurred.zip && !!errors.zip}
                  success={blurred.zip && !errors.zip}
                />
              </FormField>
            </Row>
          </Form>
        ) : (
          <div className="flex justify-center">
            {loading && <Spinner id="dashboard-spinner" size={80} />}
          </div>
        )}
      </Dialog>

      <Dialog
        id="link-policy-success"
        type={DIALOG_TYPES.SIMPLE}
        show={showSuccess}
        onClose={handleSuccessClose}
        title="Success!"
        subtitle="A new policy has been successfully added to your account."
        showSuccessIcon
        primaryButtonText="View New Policy"
        secondaryButtonText="Close"
        onPrimaryClick={handleViewNewPolicy}
        onSecondaryClick={handleSuccessClose}
        ariaLabel="Policy linked successfully"
      />
    </>
  );
};

LinkPolicy.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  api: PropTypes.object,
  history: PropTypes.object,
  fromDashboard: PropTypes.bool,
};

export default withApi(LinkPolicy);
